import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { desktopNavItems, filterNavItems, isNavGroupActive, type SubLink } from "./nav-data";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";

function NavDropdown({
  label,
  basePath,
  children,
  accentClass,
  activePaths,
}: {
  label: string;
  basePath: string;
  children: SubLink[];
  accentClass: string;
  activePaths?: string[];
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = isNavGroupActive(pathname, { basePath, activePaths });

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="group relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) scheduleClose();
      }}
    >
      <div className="inline-flex items-center gap-1">
        {/* Oberpunkt ist reiner Ordnungspunkt: kein Link, nur Auf-/Zuklappen. */}
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          data-status={isActive ? "active" : undefined}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary data-[status=active]:text-foreground"
        >
          {label}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </div>


      {/* Unsichtbare Brücke: verhindert Zuklappen zwischen Knopf und Panel. */}
      <div
        aria-hidden
        className={`absolute inset-x-0 top-full h-2 min-w-[11rem] ${open ? "" : "hidden group-hover:block group-focus-within:block"}`}
      />

      {/* Sichtbarkeit über State ODER reines CSS-Hover/Fokus – so öffnet das
          Menü auch dann, wenn Hover-Events (z. B. in der Vorschau) ausfallen. */}
      <div
        role="menu"
        className={`absolute left-0 top-full z-40 mt-2 w-max min-w-[11rem] max-w-[20rem] rounded-md border border-border bg-background p-2 shadow-lg transition-opacity ${
          open
            ? "visible opacity-100"
            : "invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
        }`}
      >
        {children.map((c) =>
          c.external ? (
            <a
              key={c.to}
              href={c.to}
              target="_blank"
              rel="noreferrer noopener"
              role="menuitem"
              onClick={() => setOpen(false)}
              className={`block whitespace-nowrap rounded-md px-3 py-2 font-display text-sm font-extrabold text-foreground transition-colors ${accentClass}`}
            >
              {c.label}
            </a>
          ) : (
            <Link
              key={c.to}
              to={c.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={`block whitespace-nowrap rounded-md px-3 py-2 font-display text-sm font-extrabold text-foreground transition-colors ${accentClass}`}
              activeProps={{ className: accentClass.replace("hover:", "") }}
            >
              {c.label}
            </Link>
          ),
        )}

      </div>
    </div>
  );
}


export function Header() {
  // Ticket ME1/KN1: verborgene Bereiche erscheinen in keinem Menü.
  const mentorings = useFeatureFlag(FEATURE_FLAGS.mentoringsVisible);
  const keynotes = useFeatureFlag(FEATURE_FLAGS.keynotesVisible);
  const navItemsToShow = filterNavItems(desktopNavItems, [
    ...(mentorings.enabled ? [FEATURE_FLAGS.mentoringsVisible] : []),
    ...(keynotes.enabled ? [FEATURE_FLAGS.keynotesVisible] : []),
  ]);
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 md:px-12">
        <Logo />

        <nav aria-label="Hauptnavigation" className="hidden items-center gap-8 lg:flex">
          {navItemsToShow.map((item) =>
            item.type === "link" ? (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm text-muted-foreground transition-colors hover:text-primary"
                activeProps={{ className: "text-foreground" }}
                activeOptions={{ exact: item.exact ?? false }}
              >
                {item.label}
              </Link>
            ) : (
              <NavDropdown
                key={item.basePath}
                label={item.label}
                basePath={item.basePath}
                children={item.children}
                activePaths={item.activePaths}
                accentClass={
                  item.basePath.startsWith("/wahlen-gewinnen")
                    ? "hover:text-brand-wahlen"
                    : item.basePath.startsWith("/politisch-kommunizieren")
                      ? "hover:text-brand-kommunikation"
                      : "hover:text-primary"
                }
              />
            ),
          )}
          <Link
            to="/kontakt"
            className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
          >
            Kontakt
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <Link
            to="/kontakt"
            className="hidden rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 md:inline-flex"
          >
            Kontakt
          </Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}
